import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { SafetyNotice } from "@/components/SafetyNotice";
import { MedicalBadge } from "@/components/MedicalBadge";
import usePageTitle from "@/hooks/usePageTitle";

export default function TherapyDetail() {
  const { slug } = useParams();
  const { data: t, isLoading, isError } = useQuery({
    queryKey: ["therapy", slug],
    queryFn: () => api.get(`/therapies/${slug}`).then((r) => r.data),
    retry: false,
  });
  usePageTitle(t?.name);

  if (isLoading) return <p className="py-24 text-center text-brand-muted">Loading…</p>;
  if (isError || !t)
    return (
      <div className="py-24 text-center">
        <p className="text-brand-muted">Therapy not found.</p>
        <Link to="/therapies" className="mt-4 inline-block text-brand underline">Back to therapies</Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl px-5 sm:px-8 pt-10">
      <Link to="/therapies" data-testid="back-to-therapies" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand">
        <ArrowLeft className="h-4 w-4" /> All therapies
      </Link>

      <div className="mt-6 flex items-center gap-1.5 text-sm text-brand-muted">
        <Clock className="h-4 w-4" /> {t.duration}
      </div>
      <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-brand-ink tracking-tight">{t.name}</h1>
      <p className="mt-4 text-xl text-brand-muted leading-relaxed">{t.summary}</p>

      {t.reviewed && (
        <div className="mt-5">
          <MedicalBadge reviewer="Ayurvedam medical board" />
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line">
        <img src={t.image} alt={t.name} className="w-full object-cover" />
      </div>

      <div className="article-body mt-8">
        <p>{t.description}</p>
      </div>

      <div className="mt-6 rounded-2xl bg-brand-mist p-6">
        <h3 className="font-heading text-xl text-brand-ink">Traditionally good for</h3>
        <ul className="mt-3 space-y-2">
          {t.good_for?.map((g, i) => (
            <li key={i} className="flex items-center gap-2.5 text-brand-ink/80">
              <CheckCircle2 className="h-4 w-4 text-brand-sage" /> {g}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 rounded-2xl border border-brand-line bg-white p-6">
        <h3 className="font-heading text-xl text-brand-ink">How to prepare</h3>
        <p className="mt-2 text-brand-ink/80">{t.preparation}</p>
      </div>

      <SafetyNotice warnings={[t.safety]} title="Safety & cautions" />
    </div>
  );
}
